if (process.env.NODE_ENV === 'development') {
  // oxlint-disable-next-line
  require('preact/debug')
}

import Widget from 'embed_v2/components/Widget'
import ModalHandler from 'embed_v2/components/widgets/ModalHandler'
import log from 'embed_v2/helpers/log'
import customHtmlForms from 'embed_v2/services/custom_html_forms'
import dayjs from 'embed_v2/services/dayjs'
import visitor from 'embed_v2/services/visitor'
import { WIDGET_TYPES } from 'embed_v2/types/version'
import fetchJsonp from 'fetch-jsonp'
import { render } from 'preact'
import qs from 'query-string'
import i18n from 'core/services/i18n'
import Secrets from 'Secrets'
import ItemApi, { type API, type Item } from './ItemApi'
import type ThankyouData from 'embed_v2/types/thankyou_data'
import type Version from 'embed_v2/types/version'

// const Widget = lazy(() => import('./components/Widget'))

class PageItems {
  uniqIds: string[]
  list: ItemApi[]

  constructor() {
    this.uniqIds = []
    this.list = []
  }

  find(id: string): API | undefined {
    return this.list.find(el => el.uniqId === id)?.api
  }

  push(item: Item) {
    const api = new ItemApi(item, this)

    this.handleItem(api)
  }

  handleItem(api: ItemApi) {
    log.info('#handleItem', api)
    window.logDifference('handleItem start', api)

    this.list.push(api)

    const getLastElement = () => {
      const mainElementId = `wf_main_${api.uniqId}`

      let mainElement = document.getElementById(mainElementId)
      if (mainElement) return mainElement

      mainElement = document.createElement('div')
      mainElement.id = mainElementId
      document.body.appendChild(mainElement)

      return mainElement
    }

    const getTargetElements = () => {
      const addBaseClasses = (el: Element) => {
        el.classList.add('wf_target', `wf_target_${api.id}`)

        return el
      }

      if (api.targetElement) {
        if (api.targetElement instanceof Element) {
          addBaseClasses(api.targetElement)
          return [api.targetElement]
        }

        if (typeof api.targetElement === 'string' && api.targetElement.length) {
          const customElements = document.querySelectorAll(api.targetElement)

          return Array.from(customElements).map(addBaseClasses)
        }
      }

      const classes = [`wf_target_${api.id}`]
      if (api.hash) {
        classes.push(`wf_target_hash_${api.hash}`)
      }
      if (api.ab) {
        classes.push(`wf_target_ab_${api.ab}`)
      }
      const elements = document.getElementsByClassName(classes.join(' '))

      if (elements.length) {
        return elements
      }

      return [getLastElement()]
    }

    const { _wf_cid: rawWfCid, source: rawSource } = qs.parse(window.location.search)
    const wf_cid = [rawWfCid].flat()[0]
    const source = [rawSource].flat()[0]

    const renderWidget = (version: Version, thankYouData: ThankyouData) => {
      api.injectCss(version)

      window.logDifference('renderWidget start', version)
      const targetElements = getTargetElements()

      switch (version.widget_type) {
        case WIDGET_TYPES.FLOATER:
          render(<Widget version={version} api={api} />, getLastElement())

          visitor.trackVisit(version, source || api.source, thankYouData?.session)

          break
        case WIDGET_TYPES.MODAL: {
          for (const targetElement of targetElements) {
            if (api.disableHandler) {
              const modalHandlers = document.querySelectorAll(
                `[href="#wf_${api.uniqId}"], [data-imagelink="#wf_${api.uniqId}"]`
              )
              if (modalHandlers.length) {
                modalHandlers.forEach(modalHandler => {
                  modalHandler.addEventListener('click', e => {
                    e.preventDefault()
                  })
                  modalHandler.addEventListener('mousedown', () => {
                    api.show()
                  })
                })
              }
            } else {
              render(<ModalHandler options={version.options.handler} api={api} />, targetElement)
            }

            render(<Widget version={version} api={api} />, getLastElement())
          }

          visitor.trackVisit(version, source || api.source)

          break
        }
        case WIDGET_TYPES.THANK_YOU:
          for (const targetElement of targetElements) {
            render(<Widget version={version} api={api} thankYouData={thankYouData} />, targetElement)
          }

          visitor.trackVisit(version, source || api.source)

          break
        case WIDGET_TYPES.REGISTRATION_PAGE: {
          const el = document.getElementById('app')

          if (el) {
            render(<Widget version={version} api={api} />, el)
          }

          break
        }
        default:
          for (const targetElement of targetElements) {
            render(<Widget version={version} api={api} />, targetElement)
          }

          visitor.trackVisit(version, source || api.source)

          break
      }

      window.logDifference('renderWidget stop', version)
    }

    const uniqItemIdList = [api.id]
    if (api.ab) {
      uniqItemIdList.push(api.ab)
    }
    const uniqItemId = uniqItemIdList.join('-')

    const urlSearchParams: URLSearchParams = new URLSearchParams({
      tz: encodeURIComponent(api.timeZone),
      rf: encodeURIComponent(window.location.href),
      rp: encodeURIComponent(`${window.location.protocol}//${window.location.host}${window.location.pathname}`)
    })

    const { versionId } = api.item

    const cid = api.cid || wf_cid || api.getSequenceCid()
    if (cid?.length) {
      urlSearchParams.append('cid', cid)
    }

    log.info('#renderWidget cid', cid, api.cid, wf_cid)

    if (versionId) {
      urlSearchParams.append('version_id', versionId.toString())
    }

    if (api.preview) {
      urlSearchParams.append('preview', 'true')
    }

    if (!this.uniqIds.includes(uniqItemId)) {
      this.uniqIds.push(uniqItemId)

      const url = new URL(`${Secrets.meetingHost}/embed/v2/widgets/${api.id}`)

      url.search = urlSearchParams.toString()
      const jsonpCallbackFunction = `jsonp_${uniqItemId.replace(/-/g, '_')}`

      fetchJsonp(url.toString(), {
        jsonpCallbackFunction,
        timeout: 10000
      })
        .then(response => response.json())
        .then(data => {
          log.info('#renderWidget response', data)
          const { locale, webinar } = data.version
          i18n.locale = locale
          if (locale === 'en') {
            renderWidget(data.version, data.thank_you_data)
          } else {
            import(/* webpackChunkName: "dayjs-[request]" */ `dayjs/locale/${locale.toLowerCase()}.js`).then(() => {
              dayjs.locale(locale)
              renderWidget(data.version, data.thank_you_data)
            })
          }
          customHtmlForms.render(webinar.cid, webinar.integration_html_forms)
        })
        .catch(e => {
          log.error(`JSONPError. Widget ID: ${api.id}`, urlSearchParams.toString(), e)
        })
    }

    window.logDifference('handleItem stop', api)
  }
}

export type PageItemsInterface = InstanceType<typeof PageItems>

export default new PageItems()
